import React, { useState } from "react";

import { api } from "../../services/api";
import { useAlert } from "../../hooks/useAlert";

import styles from "./FollowButton.module.scss";

interface FollowButtonProps {
  targetUserId: string;
  initialIsFollowing: boolean;
  isTargetPrivate?: boolean;
  initialHasPendingRequest?: boolean;
  onFollowChange?: (isFollowing: boolean) => void;
  onRequestChange?: (hasPending: boolean) => void;
}

export default function FollowButton({
  targetUserId,
  initialIsFollowing,
  isTargetPrivate = false,
  initialHasPendingRequest = false,
  onFollowChange,
  onRequestChange,
}: FollowButtonProps) {
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [hasPendingRequest, setHasPendingRequest] = useState(initialHasPendingRequest);
  const [loading, setLoading] = useState(false);
  const { confirm } = useAlert();

  const getButtonLabel = (): string => {
    if (isFollowing) return "Unfollow";
    if (hasPendingRequest) return "Requested";
    return "Follow";
  };

  const getButtonClass = (): string => {
    if (isFollowing) return styles.following;
    if (hasPendingRequest) return styles.requested;
    return styles.notFollowing;
  };

  const getAriaLabel = (): string => {
    if (isFollowing) return `Unfollow user`;
    if (hasPendingRequest) return `Cancel follow request`;
    return `Follow user`;
  };

  const handleClick = async () => {
    if (hasPendingRequest) {
      const confirmed = await confirm({
        message: "Cancel your follow request?",
        type: "warning",
        confirmText: "Yes, cancel",
        cancelText: "No",
      });
      if (!confirmed) return;
    }

    setLoading(true);

    try {
      if (isFollowing) {
        await api.social.unfollow(targetUserId);
        setIsFollowing(false);
        onFollowChange?.(false);
      } else if (hasPendingRequest) {
        await api.social.cancelFollowRequest(targetUserId);
        setHasPendingRequest(false);
        onRequestChange?.(false);
      } else {
        const response = await api.social.follow(targetUserId);

        if (response.followRequestCreated) {
          setHasPendingRequest(true);
          onRequestChange?.(true);
        } else {
          setIsFollowing(true);
          onFollowChange?.(true);
        }
      }
    } catch (error) {
      console.error("Failed to update follow state:", error);
    } finally {
      setLoading(false);
    }
  };

  const label = getButtonLabel();

  return (
    <button
      className={[styles.followButton, getButtonClass()]
        .filter(Boolean)
        .join(" ")}
      onClick={handleClick}
      disabled={loading}
      aria-label={getAriaLabel()}
    >
      {label}
    </button>
  );
}
